import React, { useState, useEffect } from 'react';
import { ChevronDown, ChevronRight, Filter, X } from 'lucide-react';
import { Badge } from './ui/badge';
import { Checkbox } from './ui/checkbox';
import axios from 'axios';

import config from '../config';

const BACKEND_URL = config.BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const TaxonomyFilter = ({ selectedCategories, selectedSubtags, onCategoryToggle, onSubtagToggle, onClearAll }) => {
  const [taxonomy, setTaxonomy] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadTaxonomy();
  }, []);

  const loadTaxonomy = async () => {
    try {
      const response = await axios.get(`${API}/taxonomy`);
      setTaxonomy(response.data.taxonomy);
      // Auto-expand categories that have selected subtags
      const newExpanded = new Set();
      response.data.taxonomy.forEach(cat => {
        if (selectedCategories.includes(cat.category) || 
            cat.subtags.some(st => selectedSubtags.includes(st.toLowerCase()))) {
          newExpanded.add(cat.category);
        }
      });
      setExpandedCategories(newExpanded);
    } catch (error) {
      console.error('Error loading taxonomy:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCategory = (category) => {
    const newExpanded = new Set(expandedCategories);
    if (newExpanded.has(category)) {
      newExpanded.delete(category);
    } else {
      newExpanded.add(category);
    }
    setExpandedCategories(newExpanded);
  };

  const isCategorySelected = (category) => {
    return selectedCategories.includes(category);
  };

  const isSubtagSelected = (subtag) => {
    return selectedSubtags.includes(subtag.toLowerCase());
  };

  const getCategorySelectionCount = (category, subtags) => {
    const selectedCount = subtags.filter(st => 
      selectedSubtags.includes(st.toLowerCase())
    ).length;
    return selectedCount;
  };

  if (loading) {
    return (
      <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="w-5 h-5 text-blue-400" />
          <h2 className="text-xl font-semibold">Taxonomy Filters</h2>
        </div>
        <div className="text-gray-400">Loading filters...</div>
      </div>
    );
  }

  const hasActiveFilters = selectedCategories.length > 0 || selectedSubtags.length > 0;

  return (
    <div className="bg-gray-800/40 backdrop-blur-sm rounded-2xl border border-gray-700/50 p-6">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Filter className="w-5 h-5 text-blue-400" />
          <h2 className="text-xl font-semibold">Taxonomy Filters</h2>
        </div>
        
        {hasActiveFilters && (
          <button
            onClick={onClearAll}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-gray-700/50 hover:bg-gray-600/50 text-gray-300 text-sm transition-colors"
          >
            <X className="w-3.5 h-3.5" />
            Clear All
          </button>
        )}
      </div>

      {/* Active Filters Summary */}
      {hasActiveFilters && (
        <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="text-sm text-blue-300 font-medium mb-2">Active Filters:</div>
          <div className="flex flex-wrap gap-2">
            {selectedCategories.map(cat => (
              <Badge
                key={cat}
                className="bg-blue-500/20 text-blue-300 hover:bg-blue-500/30 cursor-pointer"
                onClick={() => onCategoryToggle(cat)}
              >
                {cat} ✕
              </Badge>
            ))}
            {selectedSubtags.map(subtag => (
              <Badge
                key={subtag}
                className="bg-purple-500/20 text-purple-300 hover:bg-purple-500/30 cursor-pointer text-xs"
                onClick={() => onSubtagToggle(subtag)}
              >
                {subtag} ✕
              </Badge>
            ))}
          </div>
        </div>
      )}

      {/* Tree Structure */}
      <div className="space-y-2 max-h-[600px] overflow-y-auto custom-scrollbar">
        {taxonomy.map((categoryObj) => {
          const isExpanded = expandedCategories.has(categoryObj.category);
          const categorySelected = isCategorySelected(categoryObj.category);
          const subtagCount = getCategorySelectionCount(categoryObj.category, categoryObj.subtags);

          return (
            <div key={categoryObj.category} className="border border-gray-700/30 rounded-lg overflow-hidden">
              {/* Category Header */}
              <div
                className={`flex items-center gap-2 p-3 cursor-pointer transition-colors ${
                  categorySelected
                    ? 'bg-blue-500/20 hover:bg-blue-500/30'
                    : 'bg-gray-800/30 hover:bg-gray-700/30'
                }`}
              >
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    toggleCategory(categoryObj.category);
                  }}
                  className="p-1 hover:bg-gray-600/30 rounded transition-colors"
                >
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4 text-gray-400" />
                  ) : (
                    <ChevronRight className="w-4 h-4 text-gray-400" />
                  )}
                </button>

                <div className="flex items-center gap-2 flex-1" onClick={() => onCategoryToggle(categoryObj.category)}>
                  <Checkbox
                    checked={categorySelected}
                    onCheckedChange={() => onCategoryToggle(categoryObj.category)}
                    className="border-gray-500"
                  />
                  <span className={`font-medium ${categorySelected ? 'text-blue-300' : 'text-gray-200'}`}>
                    {categoryObj.category}
                  </span>
                  {subtagCount > 0 && !categorySelected && (
                    <Badge className="bg-purple-500/20 text-purple-300 text-xs">
                      {subtagCount}
                    </Badge>
                  )}
                </div>

                <span className="text-xs text-gray-500">
                  {categoryObj.subtags.length} subtags
                </span>
              </div>

              {/* Subtags */}
              {isExpanded && (
                <div className="bg-gray-900/30 p-3 space-y-1.5">
                  {categoryObj.subtags.map((subtag) => {
                    const selected = isSubtagSelected(subtag);
                    return (
                      <div
                        key={subtag}
                        className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                          selected
                            ? 'bg-purple-500/20 hover:bg-purple-500/30'
                            : 'hover:bg-gray-700/30'
                        }`}
                        onClick={() => onSubtagToggle(subtag.toLowerCase())}
                      >
                        <Checkbox
                          checked={selected}
                          onCheckedChange={() => onSubtagToggle(subtag.toLowerCase())}
                          className="border-gray-500"
                        />
                        <span className={`text-sm ${selected ? 'text-purple-300' : 'text-gray-400'}`}>
                          {subtag}
                        </span>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TaxonomyFilter;
